// Single source of truth for the managed-agent "safe" toolbelt — a PURE data
// module (no pg / ai / workflow imports) so it can be shared by:
//   - workflows/chat/workflow.ts (MANAGED_AGENT_DEFAULT_SAFE_TOOLS is derived
//     from this list — the runtime allowlist default),
//   - lib/agent-templates.ts (templates pick tools from here),
//   - the SPA agent tool-grant picker (resolved via the `@/*` core fallback).
//
// Keep this list in sync with what createChatToolsWithExternalApps actually
// registers; the safe set deliberately EXCLUDES destructive / external-messaging
// / heavy-spend tools (see the workflow.ts doc comment). Explicit allowlists can
// still opt into tools outside this catalog — this is the friendly default menu.

export interface AgentToolInfo {
  /** Tool name as registered in the chat toolbelt. */
  name: string;
  /** Human label for the picker. */
  label: string;
  /** Grouping bucket for the picker UI. */
  group: 'Web & search' | 'Visualizations' | 'History' | 'Docs & media' | 'Owner context' | 'Escalation';
}

export const AGENT_SAFE_TOOL_CATALOG: readonly AgentToolInfo[] = [
  // web + search (read-only)
  { name: 'webSearch', label: 'Web search', group: 'Web & search' },
  { name: 'imageSearch', label: 'Image search', group: 'Web & search' },
  { name: 'imageSearchBatch', label: 'Image search (batch)', group: 'Web & search' },
  { name: 'viewImages', label: 'View images', group: 'Web & search' },
  // visualizations (agent-owned artifacts)
  { name: 'mermaid', label: 'Mermaid diagrams', group: 'Visualizations' },
  { name: 'html', label: 'HTML artifact', group: 'Visualizations' },
  { name: 'htmlNative', label: 'HTML (native)', group: 'Visualizations' },
  { name: 'svg', label: 'SVG', group: 'Visualizations' },
  { name: 'listVisualizations', label: 'List visualizations', group: 'Visualizations' },
  { name: 'listVisualizationVersions', label: 'List visualization versions', group: 'Visualizations' },
  { name: 'getVisualizationSource', label: 'Read visualization source', group: 'Visualizations' },
  { name: 'grepVisualization', label: 'Search visualization', group: 'Visualizations' },
  { name: 'readVisualizationLines', label: 'Read visualization lines', group: 'Visualizations' },
  { name: 'editVisualization', label: 'Edit visualization', group: 'Visualizations' },
  { name: 'revertVisualizationVersion', label: 'Revert visualization', group: 'Visualizations' },
  { name: 'viewVisualization', label: 'View visualization', group: 'Visualizations' },
  // own-session history
  { name: 'viewHistory', label: 'View its own history', group: 'History' },
  { name: 'readTurn', label: 'Read a past turn', group: 'History' },
  { name: 'readToolResult', label: 'Read a past tool result', group: 'History' },
  // skills + docs + media (read-only)
  { name: 'listSkills', label: 'List skills', group: 'Docs & media' },
  { name: 'readSkill', label: 'Read a skill', group: 'Docs & media' },
  { name: 'listMyDocuments', label: 'List documents', group: 'Docs & media' },
  { name: 'viewDocument', label: 'View a document', group: 'Docs & media' },
  { name: 'extractDocument', label: 'Extract from a document', group: 'Docs & media' },
  { name: 'listMediaAssets', label: 'List media assets', group: 'Docs & media' },
  { name: 'viewMediaAsset', label: 'View a media asset', group: 'Docs & media' },
  { name: 'analyzeMediaAssets', label: 'Analyze media assets', group: 'Docs & media' },
  // owner context (read-only)
  { name: 'listUserMemories', label: 'List your memories', group: 'Owner context' },
  { name: 'viewFullMemory', label: 'View full memory', group: 'Owner context' },
  // HITL escalation (non-destructive by construction)
  { name: 'askUserQuestion', label: 'Ask you a question', group: 'Escalation' },
] as const;

/** Flat list of safe tool names — the runtime allowlist default. */
export const MANAGED_AGENT_DEFAULT_SAFE_TOOL_NAMES: readonly string[] =
  AGENT_SAFE_TOOL_CATALOG.map((t) => t.name);

/** Set form for O(1) membership checks (validation, template linting). */
export const MANAGED_AGENT_DEFAULT_SAFE_TOOL_SET: ReadonlySet<string> = new Set(
  MANAGED_AGENT_DEFAULT_SAFE_TOOL_NAMES,
);
